// @ts-strict-ignore
import {
  AfterViewChecked, AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { SohoBreadcrumbComponent, SohoHeaderComponent } from 'ids-enterprise-ng';
import { LMBannerHeaderComponentRef } from './lm-banner-header-component-ref.service';
import { LMToolbarComponent } from '@toolbar/lm-toolbar.component';
import { LMGlobalStateService } from '@states/services/lm-global-state.service';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import { LMGlobalState } from '@states/models/lm-global-state.model';
import { LMGlobalPersonalization } from '@states/models/lm-global-personalization.model';
import { Observable } from 'rxjs';
import { LMColorId } from './lm-theme.service';
import { LMContextService, LMRouteComponentService } from '@services/index';
import { LMBaseComponent } from '@base/lm-base.component';
import { AppConstants } from '@utils/app.constants';
import { LMApplicationMenuService } from '../applicationmenu/services/lm-application-menu.service';
import { LMReportFieldPickerService } from '../report';

import 'ids-enterprise-wc/components/ids-breadcrumb/ids-breadcrumb';
import IdsBreadcrumb from 'ids-enterprise-wc/components/ids-breadcrumb/ids-breadcrumb';
import 'ids-enterprise-wc/components/ids-hyperlink/ids-hyperlink';
import 'ids-enterprise-wc/components/ids-button/ids-button';
import { LMBreadcrumbItem, LMBreadcrumbService } from '@services/lm-breadcrumb.service';
import { LMGlobalUtilService } from '@app/utils';

@Component({
  selector: 'lm-banner-header',
  templateUrl: './lm-banner-header.component.html',
  styleUrls:   [ 'lm-banner-header.component.scss' ],
  providers: [ LMApplicationMenuService ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  })
export class LMBannerHeaderComponent implements AfterViewInit, AfterViewChecked {
  @HostListener('click', [ '$event' ]) onClick(event) {
    if ((event.ctrlKey || event.metaKey) && event.shiftKey) {
      event.stopPropagation();
      const currComp: Component = this.currentComponent.getRouteComponent();
      if (currComp instanceof LMBaseComponent) {
        const baseComp: LMBaseComponent = currComp as LMBaseComponent;
        baseComp.displayComponentInfo(event, baseComp);
      }
    }
  }

  @HostBinding('class.has-breadcrumb') get breadcrumb() {
    return this.hasBreadcrumbs;
  }
  @HostBinding('class.header') get header() {
    return true;
  }
  @HostBinding('class.is-personalizable') get isPersonalizable() {
    return true;
  }
  @HostBinding('class.has-toolbar') get hasToolbar() {
    return true;
  }

  @HostBinding('class.red-top-border') get hasBannerButton() {
    return (this.asOfDate !== undefined && this.asOfDate !== '') || this.lmContextService.isArchiveMode() 
      || this.lmContextService.hasDBSessionDebugMode() || this.lmContextService.isSecSessionDebugMode();
  }

  // @HostBinding('class.as-of-date') get hasAsOfDate() {
  //   return this.asOfDate !== undefined && this.asOfDate !== '';
  // }
  // @HostBinding('class.db-session-debug-mode') get hasDBSessionDebugMode() {
  //   return this.lmContextService.hasDBSessionDebugMode();
  // }
  // @HostBinding('class.sec-session-debug-mode') get isSecSessionDebugMode() {
  //   return this.lmContextService.isSecSessionDebugMode();
  // }
  @HostBinding('attr.id') get elementId(): string {
    return 'app_Header';
  }

  @ViewChild(LMToolbarComponent, { static: true }) lmToolbarComponent: LMToolbarComponent;
  @ViewChild('SohoHeader', { static: true }) sohoHeader: SohoHeaderComponent;
  @ViewChild(SohoBreadcrumbComponent) sohoBreadcrumbComponent: SohoBreadcrumbComponent;
  @ViewChild('breadcrumb', { read: ElementRef }) idsBreadcrumb: ElementRef | undefined;

  @Input() set toolbarSectionTitle(toolbarSectionTitle) {
    if (this.m_toolbarSectionTitle !== toolbarSectionTitle) {
      this.m_toolbarSectionTitle = toolbarSectionTitle;
      this.markForRefresh();
    }
  }
  get toolbarSectionTitle(): string {
    return this.m_toolbarSectionTitle;
  }

  @Input() set toolbarButtons(toolbarButtons: Observable<Array<LMToolbarButton>>) {
    this.m_toolbarButtons = toolbarButtons;
    this.changeDetector.markForCheck();
  }
  get toolbarButtons(): Observable<Array<LMToolbarButton>> {
    return this.m_toolbarButtons;
  }

  @Input() set toolbarSearchField(toolbarSearchField: LMToolbarSearchField) {
    this.m_toolbarSearchField = toolbarSearchField;
    this.changeDetector.markForCheck();
  }

  get toolbarSearchField(): LMToolbarSearchField {
    return this.m_toolbarSearchField;
  }

  @Input() set mustacheText(recordCount: string) {
    this.m_mustacheText = recordCount;
    this.changeDetector.markForCheck();
  }

  get mustacheText(): string {
    return this.m_mustacheText;
  }

  @Input() set disabled(disabled: boolean) {
    this.m_disabled = disabled;
    this.markForRefresh();
  }
  get disabled(): boolean {
    return this.m_disabled;
  }

  @Input() set showGoBack(show: boolean) {
    this._showGoBack = show;
    this.markForRefresh();
  }
  get showGoBack(): boolean {
    return this._showGoBack;
  }

  @Input() set breadcrumbs(breadcrumbs: SohoBreadcrumbItemOptions[] | undefined) {
    if (this.useWC) {
      this.m_breadcrumbs = breadcrumbs;
    } else {
      if (this.sohoBreadcrumbComponent) {
        this.sohoBreadcrumbComponent.removeAll(false);
      }
      this.m_sohoBreadcrumbs = breadcrumbs;
    }
    // If there is only one item in the list, do not render breadcrumbs
    this.hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 1;
    this.hasBreadcrumbEvent.emit(this.hasBreadcrumbs);
    this.markForRefresh();
  }
  get breadcrumbs(): SohoBreadcrumbItemOptions[] | undefined {
    return (this.useWC) ? this.m_breadcrumbs : this.m_sohoBreadcrumbs;
  }

  @Input() resizeToolbar: boolean;
  @Input() isListViewDetail: boolean;
  @Input() maxVisibleButtons: number;
  @Input() asOfDate: string;
  @Input() showArchive: boolean;
  @Input() toolbarMoreSettings: SohoPopupMenuOptions;
  @Input() screenAutomationId: string;
  @Input() screenId: string;

  @Output() tabActivated = new EventEmitter<SohoTabsEvent>();
  @Output() toolbarButtonEvent = new EventEmitter<LMToolbarButtonEvent>();
  @Output() toolbarSearchEvent = new EventEmitter<LMFilterEvent>();
  @Output() toolbarSearchClearedEvent = new EventEmitter<LMFilterEvent>();
  @Output() asOfDateChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() useFullPageScrolling: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() asOfDateCleared: EventEmitter<string> = new EventEmitter();
  @Output() quitArchiveViewEvent: EventEmitter<void> = new EventEmitter();
  @Output() quitDBSessionDebugModeEvent: EventEmitter<void> = new EventEmitter();
  @Output() quitSecSessionDebugModeEvent: EventEmitter<void> = new EventEmitter();
  @Output() appMenuTriggerEvent: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  @Output() hasBreadcrumbEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  private m_webAppTitle: string;
  private m_toolbarSectionTitle: string;
  private m_is3_5 = false;
  private m_documentTitleChanged = false;
  private m_toolbarButtons: Observable<Array<LMToolbarButton>>;
  private m_toolbarSearchField = null;
  private m_mustacheText = null;
  private m_disabled = false;
  private m_fullPageScroll: boolean;
  private m_breadcrumbs: SohoBreadcrumbItemOptions[] | undefined
  private m_sohoBreadcrumbs: SohoBreadcrumbItemOptions[] | undefined;

  public hasBreadcrumbs = false;
  public useWC = true;

  private _showGoBack = false;
  private bannerState: LMGlobalState = {};
  private bannerPersonalization: LMGlobalPersonalization = { theme: 'theme-new-light', color: LMColorId.default };

  public testVersion1: boolean;
  isCreateReportMode$: Observable<boolean>;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef,
    private titleService: Title,
    private lmBannerHeaderComponentRef: LMBannerHeaderComponentRef,
    private lmContextService: LMContextService,
    private currentComponent: LMRouteComponentService,
    private lmGlobalStateService: LMGlobalStateService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmReportFieldPickerService: LMReportFieldPickerService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmBreadcrumbService: LMBreadcrumbService,
  ) {
    this.lmBannerHeaderComponentRef.instance = this;
    this.loadComponentState();
    if (this.lmContextService.showBreadcrumbs() && this.bannerState?.allBreadcrumbs && this.bannerState?.breadcrumbCategory) {
      if (this.bannerState?.sessionCookie && this.bannerState.sessionCookie === this.getCSRFCookie()) {
        this.lmBreadcrumbService.initialize(this.bannerState.breadcrumbCategory, this.bannerState.allBreadcrumbs);
      } else {
        this.bannerState.allBreadcrumbs = {};
        this.bannerState.breadcrumbCategory = undefined;
      }
    }
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
    this.m_webAppTitle = this.lmContextService.getContext().webAppTitle;
    this.isCreateReportMode$ = this.lmReportFieldPickerService.isCreateReportMode$;
  }

  ngAfterViewInit() {
    // Load component state is in constructor and app component is not notified about breadcrumbs
    if (this.idsBreadcrumb) {
      this.hasBreadcrumbEvent.emit(this.hasBreadcrumbs);
    }
  }

  ngAfterViewChecked() {
    if (!this.m_documentTitleChanged) {
      return;
    }

    this.m_documentTitleChanged = false;

    if (this.toolbarSectionTitle?.length > 0) { // != null && this.toolbarSectionTitle.length && this.toolbarSectionTitle.length > 0) {
      this.titleService.setTitle(this.toolbarSectionTitle + ' / ' + this.lmContextService.getContext().webAppTitle);
    } else {
      this.titleService.setTitle(this.lmContextService.getContext().webAppTitle);
    }

    setTimeout(() => {
      if (this.idsBreadcrumb && !this.idsBreadcrumb.nativeElement.onBreadcrumbActivate) {
        this.idsBreadcrumb.nativeElement.onBreadcrumbActivate = (targetEl: HTMLElement, previousEl: HTMLElement) => {
          const id = targetEl.getAttribute('target-id');
          if (!id) {
            console.log('cannot find target-id for selected breadcrumb item');
            return;
          }
          // Activate the bread crumb item. Not able to highlight selection in BC web component
          if (this.breadcrumbs) {
            // Do not trim collection on click of an item
            // const bcItem = this.breadcrumbs.find(b => b.id === id);
            // if (bcItem) {
            //   this.lmGlobalUtilService.navigateByUrl(bcItem.href);
            // }

            // Trim the collection on click of an item
            const index = this.breadcrumbs.findIndex(b => b.id === id);
            if (index > -1 && index !== this.breadcrumbs.length - 1) {
              this.lmBreadcrumbService.spliceItems(index);
              this.breadcrumbs = structuredClone(this.lmBreadcrumbService.breadcrumbs);
              this.setBreadcrumbsInState();
              this.lmGlobalUtilService.navigateByUrl(this.breadcrumbs[index].href);
            }
          }
        };
      }
    });

  }

  onToolbarBackButtonEvent() {
    this.lmBannerHeaderComponentRef.popBackReference();
  }

  onAppMenuTriggerEvent(event: MouseEvent) {
    this.appMenuTriggerEvent.emit(event);
  }

  onToolbarButtonEvent(event) {
    this.toolbarButtonEvent.emit(event);
  }

  onToolbarSearchEvent(event) {
    this.toolbarSearchEvent.emit(event);
  }

  onToolbarSearchClearedEvent(event) {
    this.toolbarSearchClearedEvent.emit(event);
  }

  onAsOfDateChangedEvent(date: string) {
    this.asOfDate = date;
    this.asOfDateChanged.emit(date);
  }

  public onAsOfDateCleared(): void {
    this.asOfDateCleared.emit();
  }

  public onQuitArchiveView() {
    this.quitArchiveViewEvent.emit();
  }

  public onQuitDBSessionDebugMode() {
    this.quitDBSessionDebugModeEvent.emit();
  }

  public onQuitSecSessionDebugMode() {
    this.quitSecSessionDebugModeEvent.emit();
  }

  private markForRefresh() {
    this.m_documentTitleChanged = true;

    // ... make sure the change detector kicks in, otherwise if the inputs
    // were change programmatially the component may not be eligible for
    // updating.
    this.changeDetector.markForCheck();
  }

  public set is3_5(is3_5: boolean) {
    this.m_is3_5 = is3_5;
  }

  public get is3_5(): boolean {
    return this.m_is3_5;
  }

  public set fullPageScroll(fullPS: boolean) {
    this.m_fullPageScroll = fullPS;

    this.useFullPageScrolling.emit(fullPS);
  }

  loadComponentState(): void {
    this.bannerState = this.lmGlobalStateService.getGlobalState();
    if (this.lmContextService.showBreadcrumbs() && this.bannerState?.allBreadcrumbs && this.bannerState?.breadcrumbCategory) {
      this.breadcrumbs = this.bannerState.allBreadcrumbs[this.bannerState.breadcrumbCategory];
    }
    this.bannerPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
  }

  saveComponentState(): void {
    // This function is not used in banner component.
    // This method is buggy when it comes to saving banner personalization.
    // It will save it to a state captured by loadComponentState when banner was created.
    // LMWEB-19946 - The expanded menu state was overwritten by this method.
    // Breadcrumb will just call saveGlobalState instead.
    this.lmGlobalStateService.saveGlobalState(this.bannerState);
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.bannerPersonalization);
  }

  setBreadcrumbsInState(): void {
    if (this.lmContextService.showBreadcrumbs()) {
      this.bannerState = this.lmGlobalStateService.getGlobalState();
      if (!this.bannerState) {
        this.bannerState = {};
      }
      this.bannerState.allBreadcrumbs = this.lmBreadcrumbService.allBreadcrumbs;
      this.bannerState.breadcrumbCategory = this.lmBreadcrumbService.category;
      this.bannerState.sessionCookie = this.getCSRFCookie();
      this.lmGlobalStateService.saveGlobalState(this.bannerState);
    }
  }

  private getCSRFCookie(): string {
    // When a user logs out and logs back in right away in the same tab, do not display breadcrumbs
    // LMWEB-20079 - Use cookie to determine if the session is different.
    // C.LWSN is a httponly cookie. Using SSO.CSRF instead, which will be unique to a session.
    const cookies = document.cookie.split('; ');
    const csrf = 'SSO.CSRF=';
    const csrfCookie = cookies.find(c => c.indexOf(csrf) > -1);
    return (csrfCookie) ? csrfCookie.substring(csrfCookie.indexOf('=') + 1) : undefined;
  }

  public updateBreadcrumbs(bcItem: LMBreadcrumbItem): string {
    const key = this.lmBreadcrumbService.addBreadcrumbItem(bcItem);
    this.breadcrumbs = structuredClone(this.lmBreadcrumbService.breadcrumbs);
    this.setBreadcrumbsInState();
    return key;
  }

  public deleteBreadcrumb(id: string) {
    this.lmBreadcrumbService.deleteBreadcrumbItem(id);
    this.breadcrumbs = structuredClone(this.lmBreadcrumbService.breadcrumbs);
    this.setBreadcrumbsInState();
  }

  public resetBreadCrumbs(category?: string) {
    this.breadcrumbs = this.lmBreadcrumbService.reset(category);
    this.setBreadcrumbsInState();
  }

  public getCurrentCategory():string {
    return this.lmBreadcrumbService.category;
  }

  public goBackInBreadCrumbStack() {
    if (this.breadcrumbs && this.breadcrumbs.length >= 2) {
      const index = this.breadcrumbs.length - 1;
      this.lmBreadcrumbService.spliceItems(index - 1);
      this.breadcrumbs = structuredClone(this.lmBreadcrumbService.breadcrumbs);
      this.setBreadcrumbsInState();
      this.lmGlobalUtilService.navigateByUrl(this.breadcrumbs[ index - 1 ].href);
    }
  }
}
